import React from "react";
import { Typography, Button, Space } from "antd";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Scrollbar, A11y } from "swiper/modules";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import { getMediaURL } from "../../../context/API";
import {
  RankingCard,
  RankNumber,
  CategoryImage,
  StyledText,
  HeaderContainer,
  StyledTitle,
  SecondaryStyledText,
  CarouselWrapper,
  CarouselSecondaryText,
  ToggleButton,
  SwiperStyled,
  SwiperSlideStyled,
  RankingTitle,
} from "./styles";
import { addCommasInPrices, CategorySalesRankingCarouselProps } from "./constants";
import { useTheme } from "../../../context/ThemeContext";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const CategorySalesRankingCarousel: React.FC<
  CategorySalesRankingCarouselProps
> = ({ data, rankingBy, onToggleRanking }) => {
  const { isDarkMode } = useTheme();

  const getRankGradient = (rank: number): string => {
    switch (rank) {
      case 1:
        return "linear-gradient(to right, #fadb14, #faad14)";
      case 2:
        return "linear-gradient(to right, #d9d9d9, #8c8c8c)";
      case 3:
        return "linear-gradient(to right, #ff7875, #ff4d4f)";
      default:
        return "linear-gradient(to right, #bfbfbf, #8c8c8c)";
    }
  };

  const getSlidesPerView = (): number => {
    if (typeof window !== "undefined") {
      const width = window.innerWidth;
      if (width < 640) return 1;
      if (width < 768) return 2;
      if (width < 1024) return 3;
      return 4;
    }
    return 4;
  };

  const themeMode: "light" | "dark" = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  return (
    <StyledThemeProvider theme={styledTheme}>
      <CarouselWrapper>
        <HeaderContainer>
          <div>
            <StyledTitle level={2}>
              Category Sales Ranking
            </StyledTitle>
            <CarouselSecondaryText type="secondary">
              Categories ranked by{" "}
              {rankingBy === "revenue" ? "revenue" : "units sold"}.
            </CarouselSecondaryText>
          </div>
        <ToggleButton 
          size="large" 
          onClick={onToggleRanking}
          type="primary"
        >
          Rank by {rankingBy === "revenue" ? "Units Sold" : "Revenue"}
        </ToggleButton>
      </HeaderContainer>

      <SwiperStyled
        modules={[Navigation, Pagination, Scrollbar, A11y]}
        spaceBetween={20}
        slidesPerView={getSlidesPerView()}
        navigation
        pagination={{ clickable: true }}
        scrollbar={{ draggable: true }}
      >
        {data.map((category, index) => (
          <SwiperSlideStyled key={category.id}>
            <RankingCard>
              <RankNumber rank={index + 1} level={1}>
                {index + 1}
              </RankNumber>

              <CategoryImage
                src={
                  getMediaURL(category.imageUrl) ||
                  "https://via.placeholder.com/100"
                }
                alt={category.category}
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "https://via.placeholder.com/100";
                }}
              />

              <RankingTitle level={4}>
                {category.category || " - "}
              </RankingTitle>

              {rankingBy === "revenue" ? (
                <SecondaryStyledText type="secondary">
                  Revenue: $
                  {addCommasInPrices(
                    (parseFloat(String(category.totalRevenue)) || 0).toFixed(2)
                  )}
                </SecondaryStyledText>
              ) : (
                <SecondaryStyledText type="secondary">
                  Units Sold: {category.totalItemsSold}
                </SecondaryStyledText>
              )}
            </RankingCard>
          </SwiperSlideStyled>
        ))}
      </SwiperStyled>
    </CarouselWrapper>
    </StyledThemeProvider>
  );
};

export default CategorySalesRankingCarousel;
